import React, {useEffect, useState} from "react";
import * as XLSX from "xlsx";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import api from "../api";
import {apiFetch, apiFetch_} from "../apiFetch";
import ColumnMappingTable from "../components/column_mapping/ColumnMappingTable";
import {extractColumnGroups, formatPeriodName, getMappingDataType} from "../utils";

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

export default function ConfigPage() {
    const navigate = useNavigate();
    const {state} = useLocation();

    /* =========================================================
     PREVIOUS PAGE DATA
     ========================================================= */

    const existingProgram = state?.program;

    const paymentProcessResult = state?.paymentProcess;

    const hasPaymentProcess = !!paymentProcessResult;

    const iprogram_id = paymentProcessResult?.iprogram_id

    const programId = paymentProcessResult?.programId || existingProgram?.unique_id || existingProgram?.id;

    const organizationId = paymentProcessResult?.organization_id;

    const unificationRunMaster = paymentProcessResult?.unification_run_master;

    const frequencyType = paymentProcessResult?.frequency_type;

    const frequencyValue = paymentProcessResult?.frequency_value;

    console.log("Program:", existingProgram);

    console.log("Payment Process:", paymentProcessResult);

    /* =========================================================
     STATE
     ========================================================= */

    const [file1Cols,setFile1Cols] = useState([]);

    const [file2Cols,setFile2Cols] = useState([]);

    const [sourceOptions,setSourceOptions] = useState([]);

    const [targetOptions,setTargetOptions] = useState([]);

    const [mappings,setMappings] = useState([]);

    const [file1Sheets,setFile1Sheets] = useState([]);

    const [file2Sheets,setFile2Sheets] = useState([]);

    const [file1Workbook,setFile1Workbook] = useState(null);

    const [file2Workbook,setFile2Workbook] = useState(null);

    const [file1Sheet,setFile1Sheet] = useState("");

    const [file2Sheet,setFile2Sheet] = useState("");

    const [configName,setConfigName] = useState("");

    const [file1Name,setFile1Name] = useState("");

    const [file2Name,setFile2Name] = useState("");

    const [loading,setLoading] = useState(false);

    const [rules,setRules] = useState([]);

    const [programConfigDetails,setProgramConfigDetails] = useState([]);

    const [unifiedColumns,setUnifiedColumns] = useState([]);

    const [loadingMessage,setLoadingMessage] = useState("");

    /*
   * Calculation data returned from
   * payment process / Elasticsearch.
   */
    const [calculationData, setCalculationData] = useState(null);

    /* =========================================================
     GENERATE CONFIG NAME
     ========================================================= */

    useEffect(() => {
        /*
     * Manual configuration:
     * User can enter the config name.
     */
        if (!hasPaymentProcess) {
            return;
        }

        if (!frequencyType || !frequencyValue) {
            return;
        }

        const generatedConfigName = formatPeriodName(frequencyType, frequencyValue);

        if (generatedConfigName) {
            setConfigName(generatedConfigName);
        }
    }, [hasPaymentProcess, frequencyType, frequencyValue]);

    /* =========================================================
     READ EXCEL
     ========================================================= */

    const readExcel = (file, setName, isSource) => {
        if (!file) {
            return;
        }
        setName(file.name || "Payout Report");
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);

                const workbook = XLSX.read(data, {type: "array"});

                const sheets = workbook.SheetNames;

                if (isSource) {

                    setFile1Workbook(workbook);

                    setFile1Sheets(sheets);
                    if (sheets.length > 0) {

                        setFile1Sheet(sheets[0]);

                        loadColumns(workbook, sheets[0], true);
                    }
                } else {

                    setFile2Workbook(workbook);

                    setFile2Sheets(sheets);

                    if (sheets.length > 0) {

                        setFile2Sheet(sheets[0]);

                        loadColumns(workbook, sheets[0], false);
                    }
                }
            } catch (error) {
                console.error("Excel reading error:", error);
                alert("Unable to read Excel file.");
            }
        };

        reader.readAsArrayBuffer(file);
    };

    /* =========================================================
     LOAD COLUMNS
     ========================================================= */

    const loadColumns = (workbook, sheetName, isSource) => {
        if (!workbook || !sheetName) {
            return;
        }

        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) {
            return;
        }

        const json = XLSX
            .utils
            .sheet_to_json(worksheet, {header: 1});

        const columns = (json[0] || []).filter(Boolean);

        if (isSource) {
            setFile1Cols(columns);

            setSourceOptions(columns.map((c) => ({value: c, label: c})));

            /*
       * Preserve existing mapping
       * properties.
       */
            const initialMappings = columns.map((col, index) => ({
                sourceColumn: col,
                targetColumn: "",
                isUnique: false,
                displayName: false,
                isComparable: false,
                finalOutput: false,
                isInvoiceLevel: false,
                isMaster: false,
                inputColumn: false,
                customColumn: false,
                outputColumn: false,
                dataType: "Number",
                allowedDiff: 0,
                sourceColumnIndex: index,
                targetColumnIndex: -1,
                transformRuleId: null
            }));
            setMappings(initialMappings);
        } else {
            setFile2Cols(columns);
            setTargetOptions(columns.map((c) => ({value: c, label: c})));
            setMappings((prev) =>
              prev.map((mapping) => ({
                  ...mapping,
                  targetColumn: "",
                  targetColumnIndex: -1,
              }))
);
        }
    };

    /* =========================================================
     SHEET CHANGE
     ========================================================= */

    const handleSheetChange = (sheet, isSource) => {
        setMappings([])
        if (isSource) {
            setFile1Sheet(sheet);

            loadColumns(file1Workbook, sheet, true);
        } else {
            setFile2Sheet(sheet);

            loadColumns(file2Workbook, sheet, false);
        }
    };

    /* =========================================================
     UPDATE MAPPING
     ========================================================= */

    const updateMapping = (index, field, value) => {
        const updated = [...mappings];

        field = field.trim();

        if (field === "targetColumn") {
            updated[index][field] = value;

            updated[index].targetColumnIndex = file2Cols.indexOf(value);
        } else if (field === "dataType") {
            updated[index][field] = value;
        } else if (field === "transformRuleId") {
            updated[index][field] = value;
        } else if (field === "allowedDiff") {
            updated[index][field] = Number(value);
        } else if (field === "isMaster") {
            updated[index][field] = !updated[index][field];
        } else if (field === "finalOutput") {
            /*
       * Only one Final Output
       * column should be allowed.
       */

            updated.forEach((element, i) => {
                element.finalOutput = i === index
                    ? !updated[index].finalOutput
                    : false;
            });
        } else {
            updated[index][field] = !updated[index][field];
        }

        setMappings(updated);
    };

    /* =========================================================
     AUTO MAP
     ========================================================= */
    const autoMap = () => {
        if (!file2Cols || file2Cols.length === 0) {
            return;
        }

        const updated = mappings.map((mapping) => {
            if (!mapping.sourceColumn) {
                return mapping;
            }
            const match = file2Cols.find((column) => column && column.toString().toLowerCase().trim() === mapping.sourceColumn.toString().toLowerCase().trim());

            return match
                ? {
                    ...mapping,

                    targetColumn: match,

                    targetColumnIndex: file2Cols.indexOf(match)
                }
                : {
                  ...mapping,
                  targetColumn: "",
                  targetColumnIndex: -1
              };
        });

        setMappings(updated);
    };

    /* =========================================================
     APPLY CALCULATION MAPPING
     ========================================================= */

    const applyCalculationMapping = (data) => {
        if (!data) {
            return;
        }

        const {paymentPartyColumns, inputColumns, outputColumns, finalOutputColumn} = extractColumnGroups(data);

        console.log("Calculation mapping groups:", {
            paymentPartyColumns: Array.from(paymentPartyColumns),

            inputColumns: Array.from(inputColumns),

            outputColumns: Array.from(outputColumns),

            finalOutputColumn
        });

        setMappings((currentMappings) => currentMappings.map((mapping) => {
            const sourceColumn = mapping.sourceColumn?.toString().trim().toLowerCase();

            if (!sourceColumn) {
                return mapping;
            }

            /*
             * Payment Party
             */
            const isPaymentParty = paymentPartyColumns.has(sourceColumn);

            /*
             * Calculation Input
             */
            const isInput = inputColumns.has(sourceColumn);

            /*
             * Calculation Output
             */
            const isOutput = outputColumns.has(sourceColumn);

            /*
             * Final Output
             */
            const isFinalOutput = finalOutputColumn === sourceColumn;

            return {
                ...mapping,

                /*
               * Payment Party columns
               * are automatically Unique.

              isUnique:
                isPaymentParty,*/

                /*
               * Existing validation expects
               * Payment Party to be
               * isInvoiceLevel.
               */
                isInvoiceLevel: isPaymentParty,

                /*
               * Calculation inputs.
               */
                inputColumn: isInput,

                /*
               * Calculation outputs.
               */
                outputColumn: isOutput,

                /*
               * Final output.
               */
                finalOutput: isFinalOutput
            };
        }));     
    };

    /* =========================================================
     FETCH TRANSFORMATION RULES
     ========================================================= */

    useEffect(() => {
        fetchRules();
    }, []);

    const fetchRules = async() => {
        try {
            setLoading(true);

            const res = await api.get("transformation-rules");

            setRules(res
                ?.data || []);
        } catch (error) {
            console.error("Transformation rules error:", error);
        } finally {
            setLoading(false);
        }
    };

    /* =========================================================
     FETCH CALCULATION DETAILS
     ========================================================= */

    useEffect(() => {
        /*
     * Only payment-process based
     * configurations need calculation
     * details.
     */
        if (!paymentProcessResult
            ?.unique_id) {
            return;
        }

        let cancelled = false;

        const loadCalculationMapping = async() => {
            try {
                const esData = await fetchElasticSearchDetailsForPaymentProcess(paymentProcessResult.unique_id);

                if (cancelled) {
                    return;
                }

                console.log("Payment Process Calculation Data:", esData);

                setCalculationData(esData);
            } catch (error) {
                console.error("Unable to load calculation details:", error);
               setLoading(false)
            }
        };

        loadCalculationMapping();

        return () => {
            cancelled = true;
        };
    }, [
        paymentProcessResult
            ?.unique_id,
        file2Sheets
    ]);

    /* =========================================================
     APPLY CALCULATION MAPPING
     AFTER SOURCE COLUMNS EXIST
     ========================================================= */

    useEffect(() => {
        if (!calculationData || mappings.length === 0) {
            return;
        }

        applyCalculationMapping(calculationData);
        setTimeout(() => {
          setLoading(false);

        }, 1000*10);
       
    }, [calculationData, mappings.length]);

    /* =========================================================
     AUTO MAP AFTER UNIFIED COLUMNS
     ========================================================= */

    useEffect(() => {
        if (unifiedColumns.length > 0) {
            autoMap();
            
        }
    }, [unifiedColumns]);

    /* =========================================================
     ROW ACTIONS
     ========================================================= */

    const removeRow = (index) => {
        setMappings(mappings.filter((_, i) => i !== index));
    };

    const moveUp = (index) => {
        if (index === 0) {
            return;
        }

        const updated = [...mappings];

        [
            updated[index - 1],
            updated[index]
        ] = [
            updated[index],
            updated[index - 1]
        ];

        setMappings(updated);
    };

    const moveDown = (index) => {
        if (index === mappings.length - 1) {
            return;
        }

        const updated = [...mappings];

        [
            updated[index + 1],
            updated[index]
        ] = [
            updated[index],
            updated[index + 1]
        ];

        setMappings(updated);
    };

    const moveTop = (index) => {
        if (index === 0) {
            return;
        }

        const updated = [...mappings];

        const [row] = updated.splice(index, 1);

        updated.unshift(row);

        setMappings(updated);
    };

    /* =========================================================
     SAVE MAPPING
     ========================================================= */

    const saveMapping = async() => {
        if (!configName.trim()) {
            alert("Config Name is required");

            return;
        }

        if (mappings.length === 0) {
            alert("No mappings to save");

            return;
        }

        
        const hasUnique = mappings.some((m) => m.isUnique);

        if (!hasUnique) {
            alert("Select at least one Unique column");

            return;
        }
        const disPlayName = mappings.some((m) => m.displayName);
        if (!disPlayName) {
            alert("Please select a column to use as the Display Name (e.g., Agency Name, User Name, etc.).");
            return;
        }


        const hasInvoiceLevel = mappings.some((m) => m.isInvoiceLevel);

        if (!hasInvoiceLevel) {
            alert("Select at least one Payment Party column");

            return;
        }

        const hasFinalOutput = mappings.some((m) => m.finalOutput);

        if (!hasFinalOutput) {
            alert("Select at least one Final Output column");

            return;
        }
        if (!programId) {
            alert("Program ID is missing");

            return;
        }

        if (!unificationRunMaster && hasPaymentProcess) {
            alert("Unification Run Master ID is missing");

            return;
        }

        try {
            setLoading(true);

            const payload = {
                configName,
                file1Name,
                file2Name,
                mappings,
                file1Sheet,
                file2Sheet,
                programId,
                unificationRunMaster,
                frequencyType,
                frequencyValue,
                periodTitle: configName,
                esData:calculationData
            };

            console.log("Saving configuration payload:", payload);

            await apiFetch("file-mapping-config", {
                method: "POST",

                headers: {
                    "Content-Type": "application/json"
                },

                body: JSON.stringify(payload)
            }).then((res) => {
                if (!res.ok) {
                    throw new Error(`API failed: ${res.status}`);
                }

                return res.json();
            }).then((data) => {
                alert("Configuration Saved Successfully!");
            })
            setConfigName("");

            navigate("/");
        } catch (error) {
            console.error("Error saving configuration:", error);

            alert("Error saving configuration");
        } finally {
            setLoading(false);
        }
    };

    /* =========================================================
     DOWNLOAD PAYOUT REPORT
     ========================================================= */

    useEffect(() => {
        /*
     * Manual configuration:
     * Do not download payout report.
     */
        if (!hasPaymentProcess) {
            console.log("No payment process. Waiting for manual file upload.");

            return;
        }

        if (!paymentProcessResult) {
            console.warn("Payment process details not available.");

            return;
        }

        if (!unificationRunMaster) {
            console.warn("unification_run_master not available.");

            return;
        }

        if (!frequencyType || !frequencyValue) {
            console.warn("Frequency information not available.");

            return;
        }

        const periodTitle = formatPeriodName(frequencyType, frequencyValue);

        if (!periodTitle) {
            console.warn("Unable to generate period title.");

            return;
        }

        const url = process.env.REACT_APP_PAYPROMPT_URL + "app/api/v1/payment_management/download-payout-report/" + `?unification_master_id=${encodeURIComponent(unificationRunMaster)}` + `&period_title=${encodeURIComponent(periodTitle)}`;

        console.log("Downloading payout report:", {
            unification_master_id: unificationRunMaster,

            period_title: periodTitle,

            url
        });
        setLoadingMessage("Downloading Report File!");
        setLoading(true);

        apiFetch_(url, {
            method: "GET",

            headers: {
                "Content-Type": "application/json",

                "iprogram-id": iprogram_id,

                "organization-id": organizationId
            }
        }).then(async(res) => {
            if (!res.ok) {
                throw new Error(`API failed: ${res.status}`);
            }

            const blob = await res.blob();

            console.log("Excel Blob:", blob);

            return blob;
        }).then((blob) => {
            /*
         * Same payout report used
         * for source and target.
         */

            readExcel(blob, setFile1Name, true);

            readExcel(blob, setFile2Name, false);

            /*
         * Fetch existing configuration
         * metadata.
         */

            getProgramConfigDetails();
        }).catch((error) => {
            console.error("Payout report API error:", error);

            alert("Unable to download payout report.");
        }). finally(() => {
            //setLoading(false);
        });
    }, [
        paymentProcessResult,
        unificationRunMaster,
        frequencyType,
        frequencyValue,
        programId,
        organizationId
    ]);

    /* =========================================================
     PROGRAM CONFIG DETAILS
     ========================================================= */

    function getProgramConfigDetails() {
        if (!programId) {
            console.warn("Program ID not available.");
            setLoading(false);
            return;
        }
        setLoadingMessage("Fetching Program Configuration!")
        apiFetch_(`${process.env.REACT_APP_PAYPROMPT_URL}program/api/v1/program_configuration_management/program_config_details/`, {
            method: "GET",

            headers: {
                "Content-Type": "application/json",

                "iprogram-id": iprogram_id,

                "organization-id": organizationId
            }
        }).then((res) => {
            if (!res.ok) {
                throw new Error(`API failed: ${res.status}`);
            }

            return res.json();
        }).then((data) => {
            console.log("getProgramConfigDetails():", data);

            const configs = data
                ?.results || [];

            setProgramConfigDetails(configs);

            configs.forEach((element) => {
                element.rule_plan_unified_list
                    ?.forEach((unified) => {
                        if (unified
                            ?.unified_source_master_id) {
                            getUnifiledDetilas(unified.unified_source_master_id);
                        }
                    });
            });
        }).catch((error) => {
            console.error("getProgramConfigDetails() API error:", error);
            setLoading(false);
        });
    }

    /* =========================================================
     UNIFIED SOURCE DETAILS
     ========================================================= */

    function getUnifiledDetilas(unified_source_master_id) {
        if (!unified_source_master_id) {
            return;
        }

        setLoadingMessage("Fetching Data Columns!")

        const url = `${process.env.REACT_APP_PAYPROMPT_URL}data/api/v1/unified_data_source/data_source_column_list/` + `?id=${encodeURIComponent(unified_source_master_id)}` + "&source_type=unified";

        apiFetch_(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "iprogram-id": iprogram_id,
                "organization-id": organizationId
            }
        }).then((res) => {
            if (!res.ok) {
                throw new Error(`API failed: ${res.status}`);
            }

            return res.json();
        }).then((data) => {
            console.log("getUnifiledDetilas():", data);

            const columns = data
                ?.results
                    ?.column_list || [];

            setUnifiedColumns(columns);

            /*
         * Update existing mappings
         * with API metadata.
         *
         * This DOES NOT replace
         * calculation flags.
         */

            setMappings((currentMappings) => currentMappings.map((mapping) => {
                const apiColumn = columns.find((column) => column.label
                    ?.toLowerCase().trim() === mapping.sourceColumn
                        ?.toLowerCase().trim());

                if (!apiColumn) {
                    return mapping;
                }

                return {
                    ...mapping,

                    dataType: getMappingDataType(apiColumn.data_type),

                    customColumn: apiColumn.is_custom === true || apiColumn.origin_is_custom === true,
                    isUnique: apiColumn
                        ?.is_unique_column
                            ? apiColumn
                                ?.is_unique_column
                                : false
                };
            }));
        }).catch((error) => {
            console.error("getUnifiledDetilas() API error:", error);
            setLoading(false);
        });
    }

    /* =========================================================
     FETCH ELASTICSEARCH DATA
     http://127.0.0.1:8004/api/v1/payment_management/get_elastic_search_data/?payment_process_id=06a61a3d-38ad-7ac7-8000-0000be647723

     ========================================================= */

    async function fetchElasticSearchDetailsForPaymentProcess(paymentProcessId) {

        setLoadingMessage("Fetching Column Type!")

        const response = await apiFetch_(`${process.env.REACT_APP_PAYPROMPT_URL}app/api/v1/payment_management/get_elastic_search_data/?payment_process_id=${paymentProcessId}`, 
          {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "skip-permission": process.env.REACT_APP_SKIP_PERMISSION
            }
        });
        if (!response.ok) {
              throw new Error(`Payment process
                  API failed with status ${response.status}`);
        }       
        console.log("Fetching calculation details for paymentprocess:",   paymentProcessId );
        return await response.json(); 
    }

    /* =========================================================
     RENDER
     ========================================================= */

    return (<>
    {loading && <PageLoader message={loadingMessage} />}
      
        <div className="bg-gray-50 min-h-screen p-6 font-display text-slate-900 flex flex-col">
            <main className="flex-1 px-6 md:px-10 py-8 max-w-[1440px] mx-auto w-full">

                {/* =====================================================
            HEADER
            ===================================================== */}

                <div className="mb-8">
                    <h1 className="text-2xl font-black">
                        Configure Reconciliation Mapping
                    </h1>

                    <p className="text-slate-500 mt-2">
                        Define how columns align between source and target files.
                    </p>

                    <div className="mt-3">
                        <h4 className="font-semibold">
                            Program Name:<span className="text-primary">{" "} {paymentProcessResult
                                ?.iprogram_name || existingProgram
                                    ?.programName || existingProgram
                                        ?.name || "-"}</span>
                        </h4>
                    </div>
                </div>

                {/* =====================================================
            PAYMENT PROCESS INFORMATION
            Only shown when payment process exists
            ===================================================== */}

                {hasPaymentProcess && (
                    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6 shadow-sm">

                        <div className="flex items-center justify-between mb-4">

                            <div>
                                <h3 className="text-lg font-bold text-slate-900">
                                    Payment Process
                                </h3>

                                <p className="text-xs text-slate-500 mt-1">
                                    Current payment process period
                                </p>
                            </div>

                            {/* <div className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold">
                                {paymentProcessResult?.status ||
                                "Ready"}
                            </div> */}

                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                            {/* Frequency */}
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                                <div className="text-xs text-slate-500 mb-1">
                                    Frequency
                                </div>

                                <div className="font-bold text-slate-900">
                                    {paymentProcessResult
                                        ?.frequency_name || frequencyType || "-"}
                                </div>
                            </div>

                            {/* Payment Period */}
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                                <div className="text-xs text-slate-500 mb-1">
                                    Payment Period
                                </div>

                                <div className="font-bold text-slate-900">
                                    {frequencyValue || "-"}
                                </div>
                            </div>

                            {/* Generated Config */}
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                                <div className="text-xs text-slate-500 mb-1">
                                    Configuration Period
                                </div>

                                <div className="font-bold text-primary">
                                    {configName || "Generating..."}
                                </div>
                            </div>

                        </div>
                    </div>
                )}

                {/* =====================================================
            CONFIG NAME
            ===================================================== */}

                <div className="bg-white border border-slate-200 p-6 rounded-xl mb-6 shadow-sm">

                    <label className="block text-sm font-bold text-slate-700 mb-2">
                        Config Name
                    </label>

                    <input
                        type="text"
                        placeholder="Enter Config Name"
                        value={configName}
                        readOnly={hasPaymentProcess}
                        onChange={(e) => !hasPaymentProcess && setConfigName(e.target.value)}
                        className={`h-12 w-full border rounded px-4 ${hasPaymentProcess
                        ? "bg-slate-100 border-slate-200 text-slate-700 font-semibold cursor-not-allowed"
                        : "bg-white border-slate-300 text-slate-900"}`}/>

                    <p className="text-xs text-slate-500 mt-2">
                        {hasPaymentProcess
                            ? "Automatically generated from the payment process period."
                            : "Enter a name for this reconciliation configuration."}
                    </p>
                </div>

                {/* =====================================================
            FILE & SHEET SELECTION
            ===================================================== */}

                <div className="bg-white border border-slate-200 p-6 rounded-xl mb-6 shadow-sm">

                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <span className="material-symbols-outlined text-primary">
                            layers
                        </span>

                        File & Sheet Selection
                    </h3>

                    <div className="grid md:grid-cols-2 gap-6 mb-6">

                        {/* SOURCE */}

                        <div className="flex flex-col gap-4">

                            <UploadBox
                                title="Source File"
                                fileName={file1Name}
                                onChange={(file) => readExcel(file, setFile1Name, true)}/> {file1Sheets.length > 0 && (
                                <select
                                    value={file1Sheet}
                                    onChange={(e) => handleSheetChange(e.target.value, true)}
                                    className="h-12 border rounded px-3 bg-white">
                                    {file1Sheets.map((sheet, index) => (
                                        <option key={index} value={sheet}>
                                            {sheet
}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {/* TARGET */}

                        <div className="flex flex-col gap-4">

                            <UploadBox
                                title="Target File"
                                fileName={file2Name}
                                onChange={(file) => readExcel(file, setFile2Name, false)}/> {file2Sheets.length > 0 && (
                                <select
                                    value={file2Sheet}
                                    onChange={(e) => handleSheetChange(e.target.value, false)}
                                    className="h-12 border rounded px-3 bg-white">
                                    {file2Sheets.map((sheet, index) => (
                                        <option key={index} value={sheet}>
                                            {sheet
}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                    </div>
                </div>

                {/* =====================================================
            MAPPING TABLE
            ===================================================== */}

                {mappings.length > 0 && ( <> <ColumnMappingTable
                        mappings={mappings}
                        file2Cols={file2Cols}
                        rules={rules}
                        autoMap={autoMap}
                        updateMapping={updateMapping}
                        moveTop={moveTop}
                        moveUp={moveUp}
                        moveDown={moveDown}
                        removeRow={removeRow}
                        showEdit={false}/>

                {/* =================================================
                  VALIDATION & ACTIONS
                ================================================= */
                } 
                <div className = "flex flex-col md:flex-row items-center justify-between gap-6 bg-white border border-slate-200 p-6 rounded-xl mb-6 shadow-lg" > <div className="flex items-center gap-4">
                            <div
                                className="size-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100">

                                <span className="material-symbols-outlined">
                                    verified
                                </span>

                            </div>

                            <div>
                                <h4 className="text-sm font-bold text-slate-900">
                                    Validation Status
                                </h4>

                                <p className="text-xs text-slate-500 mt-1">
                                    Select required Unique, Payment Party and Final Output columns before saving.
                                </p>
                            </div>
                        </div> < div className = "flex gap-4 w-full md:w-auto" > <button
                            onClick={saveMapping}
                            disabled={loading}
                            className="flex-1 md:flex-none h-12 px-8 rounded-lg bg-primary text-white font-black shadow-lg shadow-primary/10 hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                            {loading? "Saving...": "Save Configuration"}
                        </button> 
                      </div>
                </div > 
            </>)}
            </main>
        </div>
   </> );
}

/* =========================================================
   UPLOAD BOX
   ========================================================= */

function UploadBox({title, fileName, onChange}) {
    return (
        <label
            className="border-2 border-dashed border-slate-300 p-6 rounded-xl text-center cursor-pointer hover:border-primary transition-colors">

            <p className="font-bold text-slate-800">
                {title}
            </p>

            <p className="text-sm text-slate-500 mt-1 truncate">
                {fileName || "Choose Excel file"}
            </p>

            <input
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                hidden
                onChange={(e) => {
                const file = e.target.files
                    ?.[0];
                if (file) {
                    onChange(file);
                }
            }}/>
        </label>
    );
}

function PageLoader({message}) {
  return (
    <div className="fixed inset-0 z-[9999] bg-white/60 flex items-center justify-center">
      <div className="flex flex-col items-center">
        <div className="h-12 w-12 rounded-full border-4 border-slate-300 border-t-primary animate-spin" />
        <p className="mt-3 text-sm font-medium text-slate-700">
          Please wait...
        </p>
        <p className="mt-4 text-sm font-medium text-slate-600">
          {message}
        </p>
      </div>
    </div>
  );
}